import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import DropService from '../services/DropService';
import DropMapPicker from '../components/DropMapPicker';
import { 
  FiMapPin, 
  FiGift, 
  FiDollarSign, 
  FiClock, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiArrowLeft,
  FiAward,
  FiTag,
  FiFilm,
  FiShield,
  FiRefreshCw
} from 'react-icons/fi';

const EditDrop = () => {
  const history = useHistory();
  const { id } = useParams();
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [locationName, setLocationName] = useState('');
  const [latitude, setLatitude] = useState(28.613939);
  const [longitude, setLongitude] = useState(77.209021);
  const [expiryHours, setExpiryHours] = useState('24');
  const [claimRadius, setClaimRadius] = useState(100);

  // Drop & Hunt 2.0 Configuration State
  const [dropType, setDropType] = useState('DIGITAL');
  const [lives, setLives] = useState(3);
  const [lifelinePurchaseEnabled, setLifelinePurchaseEnabled] = useState(true);
  const [lifelinePurchaseQuantity, setLifelinePurchaseQuantity] = useState(3);
  const [lifelinePurchasePrice, setLifelinePurchasePrice] = useState(20);

  // Reward Category & Config State
  const [rewardType, setRewardType] = useState('CASH');
  const [rewardValueDetails, setRewardValueDetails] = useState('');
  const [estimatedValue, setEstimatedValue] = useState(500);
  const [totalPrizePool, setTotalPrizePool] = useState(500);
  const [totalWinners, setTotalWinners] = useState(1);

  // Physical Hunt OTP / Passcode & Participant Limit State
  const [isOtpRequired, setIsOtpRequired] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [hunterLimit, setHunterLimit] = useState(0); // 0 = unlimited

  const isCashType = rewardType === 'CASH';

  useEffect(() => {
    fetchDropDetails();
  }, [id]);

  const fetchDropDetails = async () => {
    setFetching(true);
    setErrorMessage('');
    try {
      const res = await DropService.getDropById(id);
      const drop = res.drop || res.data || res;
      if (drop) {
        setTitle(drop.title || '');
        setDescription(drop.description || '');
        setStatus(drop.status || 'ACTIVE');
        setDropType(drop.type || 'DIGITAL');
        setIsOtpRequired(drop.isOtpRequired === true);
        setOtpCode(drop.otpCode || '');
        setHunterLimit(drop.hunterLimit || 0);
        setLives(drop.lives || 3);
        setLifelinePurchaseEnabled(drop.lifelinePurchaseEnabled !== false);
        setLifelinePurchaseQuantity(drop.lifelinePurchaseQuantity || 3);
        setLifelinePurchasePrice(drop.lifelinePurchasePrice || 20);
        setLocationName(drop.locationName || '');
        setLatitude(drop.latitude || 28.613939);
        setLongitude(drop.longitude || 77.209021);
        setClaimRadius(drop.claimRadius || 100);

        const rwType = drop.rewardType || 'CASH';
        setRewardType(rwType);
        
        const prize = drop.rewardAmount || drop.rewardConfig?.totalPrizePool || 500;
        setTotalPrizePool(prize);
        setEstimatedValue(prize);
        setTotalWinners(drop.rewardConfig?.totalWinners || drop.maxWinners || 1);
        setRewardValueDetails(drop.rewardValue || '');
      } else {
        setErrorMessage('Drop not found');
      }
    } catch (err) {
      console.error('Error fetching drop details for edit:', err);
      setErrorMessage(err.response?.data?.message || err.message || 'Failed to load drop details');
    } finally {
      setFetching(false);
    }
  };

  const handleLocationChange = (lat, lng, address) => {
    setLatitude(lat);
    setLongitude(lng);
    if (address && !locationName) {
      setLocationName(address.split(',')[0] || address);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!title.trim()) {
      setErrorMessage('Please enter a Title for the GPS Drop.');
      return;
    }
    if (!latitude || !longitude) {
      setErrorMessage('Please select a valid GPS location on the map.');
      return;
    }
    if (isCashType && totalPrizePool <= 0) {
      setErrorMessage('Cash Prize Pool must be greater than ₹0.');
      return;
    }
    if (dropType === 'PHYSICAL' && isOtpRequired && !otpCode.trim()) {
      setErrorMessage('Please enter an OTP or secret Passcode string for this Physical Hunt.');
      return;
    }

    setLoading(true);

    try {
      let finalRewardVal = isCashType 
        ? `₹${totalPrizePool} Total Cash Prize Pool` 
        : (rewardValueDetails.trim() || `${rewardType === 'TICKET' ? 'Movie / Event Pass' : rewardType === 'GIFT_CARD' ? 'Gift Card' : 'Sponsored Gift'} (₹${estimatedValue} Value)`);
      let rankRewards = [];

      if (totalWinners === 1) {
        rankRewards = [
          {
            fromRank: 1,
            toRank: 1,
            rewardType: rewardType,
            rewardValue: finalRewardVal,
            rewardAmount: isCashType ? totalPrizePool : estimatedValue,
          },
        ];
      } else {
        const amountPerTier = isCashType ? Math.floor(totalPrizePool / totalWinners) : estimatedValue;
        rankRewards = [
          {
            fromRank: 1,
            toRank: totalWinners,
            rewardType: rewardType,
            rewardValue: finalRewardVal,
            rewardAmount: amountPerTier,
          },
        ];
      }

      const payload = {
        title: title.trim(),
        description: description.trim() || `${rewardType} reward waiting inside search zone!`,
        type: dropType,
        status: status,
        lives: parseInt(lives),
        lifelinePurchaseEnabled,
        lifelinePurchaseQuantity: parseInt(lifelinePurchaseQuantity),
        lifelinePurchasePrice: parseFloat(lifelinePurchasePrice),
        maxWinners: parseInt(totalWinners),
        // Physical Hunt OTP & Hunter Limit
        isOtpRequired: dropType === 'PHYSICAL' && isOtpRequired,
        otpCode: dropType === 'PHYSICAL' && isOtpRequired ? otpCode.trim() : '',
        hunterLimit: dropType === 'PHYSICAL' ? (parseInt(hunterLimit) || 0) : 0,
        latitude,
        longitude,
        locationName: locationName.trim() || 'Search Zone Area',
        claimRadius: parseInt(claimRadius),
        visibilityRadius: 10000,
        expiryHours: parseInt(expiryHours),
        rewardType: rewardType,
        rewardValue: finalRewardVal,
        rewardAmount: isCashType ? totalPrizePool : estimatedValue,
        rewardConfig: {
          totalWinners: parseInt(totalWinners),
          totalPrizePool: isCashType ? parseFloat(totalPrizePool) : parseFloat(estimatedValue),
          rewards: rankRewards,
        },
      };

      const res = await DropService.updateDrop(id, payload);
      if (res.success || res.drop) {
        setSuccessMessage('GPS Drop updated successfully!');
        setTimeout(() => {
          history.push('/app/drops');
        }, 1200);
      } else {
        setErrorMessage(res.message || 'Failed to update GPS Drop.');
      }
    } catch (err) {
      console.error('Error updating drop:', err);
      setErrorMessage(err.response?.data?.message || err.message || 'Error updating GPS Drop.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="p-12 text-center text-slate-400 text-xs font-semibold space-y-3">
        <FiRefreshCw className="w-6 h-6 animate-spin mx-auto text-purple-600" />
        <p>Loading Drop details...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => history.goBack()}
            className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              <span>Edit GPS Drop</span>
              <span className="text-xs bg-purple-100 text-purple-800 font-bold px-2.5 py-0.5 rounded-full border border-purple-200 uppercase flex items-center gap-1">
                <FiShield className="w-3 h-3" />
                <span>Admin Edit</span>
              </span>
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Update drop title, status, location, rewards, or expiry settings.
            </p>
          </div>
        </div>
      </div>

      {/* Alert Messages */}
      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3 text-red-700 text-xs font-semibold">
          <FiAlertCircle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-3 text-emerald-700 text-xs font-semibold">
          <FiCheckCircle className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Form Card */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Drop Overview & Status */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <FiGift className="text-purple-700 w-4 h-4" />
            <span>1. Drop Overview & Status</span>
          </h2>

          <div className="grid grid-cols-1 gap-4">
            {/* Status & Type Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Drop Status *</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                >
                  <option value="ACTIVE">🟢 ACTIVE (Live on Map)</option>
                  <option value="HUNTING">⚡ HUNTING (Hunter Active)</option>
                  <option value="CLAIMED">🎉 CLAIMED (Fully Claimed)</option>
                  <option value="EXPIRED">⏳ EXPIRED (Time Up)</option>
                  <option value="CANCELLED">🚫 CANCELLED (Disabled)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Drop Type *</label>
                <select
                  value={dropType}
                  onChange={(e) => setDropType(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                >
                  <option value="DIGITAL">🟣 DIGITAL HUNT (Location Guessing)</option>
                  <option value="PHYSICAL">🟢 PHYSICAL HUNT (Real-World Navigation)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Drop Title *</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Official Admin ₹1000 Cash Drop @ Connaught Place!"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Short Description / Clue</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Give hunters a hint or instructions on how to claim their reward..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white transition-all"
              />
            </div>

            {/* Lifelines & Purchase Controls */}
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
              <span className="text-xs font-bold text-slate-900 block uppercase tracking-wider">Lifelines & Purchase Configuration</span>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Starting Lives ❤️ *</label>
                  <select
                    value={lives}
                    onChange={(e) => setLives(parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900"
                  >
                    <option value={3}>3 Lives (Standard)</option>
                    <option value={5}>5 Lives (Extended)</option>
                    <option value={10}>10 Lives (Mega Hunt)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Allow Lifeline Purchase</label>
                  <select
                    value={lifelinePurchaseEnabled ? 'YES' : 'NO'}
                    onChange={(e) => setLifelinePurchaseEnabled(e.target.value === 'YES')}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900"
                  >
                    <option value="YES">ENABLED (Users can buy extra lives)</option>
                    <option value="NO">DISABLED (Strict limit)</option>
                  </select>
                </div>

                {lifelinePurchaseEnabled && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Purchase Price (₹) & Quantity</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={lifelinePurchasePrice}
                        onChange={(e) => setLifelinePurchasePrice(parseFloat(e.target.value) || 20)}
                        placeholder="Price ₹"
                        className="w-1/2 px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900"
                      />
                      <input
                        type="number"
                        min="1"
                        value={lifelinePurchaseQuantity}
                        onChange={(e) => setLifelinePurchaseQuantity(parseInt(e.target.value) || 3)}
                        placeholder="Extra Lives"
                        className="w-1/2 px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Physical Hunt Access & Security Settings (OTP / Passcode & Hunter Limit) */}
            {dropType === 'PHYSICAL' && (
              <div className="p-4.5 bg-emerald-50/70 border-2 border-emerald-300 rounded-2xl space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-black text-emerald-950 uppercase tracking-wider flex items-center gap-1.5">
                      <span>🔒 Physical Hunt Access & Participant Rules</span>
                    </span>
                    <p className="text-[11px] text-emerald-800 font-medium mt-0.5">
                      Configure OTP / secret passcode requirements and limit maximum hunter participants.
                    </p>
                  </div>
                  <span className="text-[10px] bg-emerald-200 text-emerald-900 font-extrabold px-2 py-0.5 rounded-md uppercase">
                    Physical Only
                  </span>
                </div>

                {/* OTP / Passcode Toggle & Input */}
                <div className="bg-white p-3.5 rounded-xl border border-emerald-200 space-y-3">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isOtpRequired}
                      onChange={(e) => setIsOtpRequired(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 rounded-sm focus:ring-emerald-500"
                    />
                    <div>
                      <span className="text-xs font-bold text-slate-900 block">Require OTP / Passcode to Start Hunt</span>
                      <span className="text-[10px] text-slate-500 font-medium block">
                        Users must enter this exact code in the app before they can start hunting.
                      </span>
                    </div>
                  </label>

                  {isOtpRequired && (
                    <div className="pt-2 border-t border-slate-100">
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Secret Passcode / OTP String *
                      </label>
                      <input
                        type="text"
                        required={isOtpRequired}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        placeholder="e.g. 7492 or HUNT2026 or SECRET_PASS"
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-emerald-600 focus:bg-white tracking-wider"
                      />
                      <p className="text-[10px] text-slate-500 mt-1 font-medium">
                        Share this code with players at the physical event or on your campaign materials.
                      </p>
                    </div>
                  )}
                </div>

                {/* Hunter Limit Input */}
                <div className="bg-white p-3.5 rounded-xl border border-emerald-200">
                  <label className="block text-xs font-bold text-slate-900 mb-1">
                    Participant Limit (Maximum Hunters Allowed)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="0"
                      value={hunterLimit}
                      onChange={(e) => setHunterLimit(Math.max(0, parseInt(e.target.value) || 0))}
                      placeholder="0 (Unlimited)"
                      className="w-36 px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-emerald-600 focus:bg-white"
                    />
                    <span className="text-[11px] text-slate-600 font-medium">
                      {hunterLimit === 0 ? '✨ Unlimited hunters can start this hunt' : `👥 Only ${hunterLimit} people can start this hunt`}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1 font-medium">
                    e.g. Set to 10 so only the first 10 users can join and start navigation. Leave as 0 for unlimited.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
                  <FiClock className="text-slate-400" />
                  <span>Extend Expiry Hours (Optional)</span>
                </label>
                <select
                  value={expiryHours}
                  onChange={(e) => setExpiryHours(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                >
                  <option value="12">+12 Hours from now</option>
                  <option value="24">+24 Hours (1 Day) from now</option>
                  <option value="48">+48 Hours (2 Days) from now</option>
                  <option value="72">+72 Hours (3 Days) from now</option>
                  <option value="168">+1 Week (7 Days) from now</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Claim Radius *</label>
                <select
                  value={claimRadius}
                  onChange={(e) => setClaimRadius(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                >
                  <option value="50">50 Meters (Tight Spot)</option>
                  <option value="100">100 Meters (Standard Venue Zone)</option>
                  <option value="250">250 Meters (Wide Venue/Campus)</option>
                  <option value="500">500 Meters (City Area)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Reward Category Setup */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <FiAward className="text-amber-500 w-4 h-4" />
            <span>2. Reward Setup</span>
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              type="button"
              onClick={() => setRewardType('CASH')}
              className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between h-24 ${
                rewardType === 'CASH'
                  ? 'bg-purple-50 border-purple-600 text-purple-900 font-bold shadow-sm'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              <FiDollarSign className="w-5 h-5 text-emerald-600" />
              <div>
                <span className="text-xs font-black block">Cash Pool</span>
                <span className="text-[10px] opacity-70 block font-normal">Direct Cash</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setRewardType('TICKET')}
              className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between h-24 ${
                rewardType === 'TICKET'
                  ? 'bg-purple-50 border-purple-600 text-purple-900 font-bold shadow-sm'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              <FiFilm className="w-5 h-5 text-indigo-600" />
              <div>
                <span className="text-xs font-black block">Movie / Event Pass</span>
                <span className="text-[10px] opacity-70 block font-normal">Ticket Pass</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setRewardType('GIFT_CARD')}
              className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between h-24 ${
                rewardType === 'GIFT_CARD'
                  ? 'bg-purple-50 border-purple-600 text-purple-900 font-bold shadow-sm'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              <FiGift className="w-5 h-5 text-amber-600" />
              <div>
                <span className="text-xs font-black block">Gift Card</span>
                <span className="text-[10px] opacity-70 block font-normal">Amazon/Starbucks</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setRewardType('SPONSORED_REWARD')}
              className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between h-24 ${
                rewardType === 'SPONSORED_REWARD'
                  ? 'bg-purple-50 border-purple-600 text-purple-900 font-bold shadow-sm'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              <FiTag className="w-5 h-5 text-rose-600" />
              <div>
                <span className="text-xs font-black block">Sponsored Gift</span>
                <span className="text-[10px] opacity-70 block font-normal">Physical Gift/Coupon</span>
              </div>
            </button>
          </div>

          {isCashType ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Total Cash Prize Pool (₹) *</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-slate-500 text-sm">₹</span>
                  <input
                    type="number"
                    min="10"
                    step="10"
                    required
                    value={totalPrizePool}
                    onChange={(e) => setTotalPrizePool(Math.max(10, parseInt(e.target.value) || 0))}
                    className="w-full pl-8 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-black text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Number of Winners *</label>
                <select
                  value={totalWinners}
                  onChange={(e) => setTotalWinners(parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                >
                  <option value={1}>1 Winner (100% to Winner)</option>
                  <option value={3}>3 Winners (Split Tier)</option>
                  <option value={5}>5 Winners (Split Tier)</option>
                  <option value={7}>7 Winners (Split Tier)</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Reward Voucher Code / Claim Instructions *
                </label>
                <input
                  type="text"
                  required
                  value={rewardValueDetails}
                  onChange={(e) => setRewardValueDetails(e.target.value)}
                  placeholder="e.g. VIP Pass Code: TAPCOE-9982"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Estimated Voucher Value (₹)</label>
                  <input
                    type="number"
                    min="10"
                    value={estimatedValue}
                    onChange={(e) => setEstimatedValue(parseInt(e.target.value) || 100)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Total Winners</label>
                  <select
                    value={totalWinners}
                    onChange={(e) => setTotalWinners(parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
                  >
                    <option value={1}>1 Winner Pass</option>
                    <option value={3}>3 Winners</option>
                    <option value={5}>5 Winners</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Interactive Location Map Picker */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <FiMapPin className="text-purple-700 w-4 h-4" />
            <span>3. Drop GPS Location & Map</span>
          </h2>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Venue / Location Name *</label>
            <input
              type="text"
              required
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g. Sector 17 Plaza, Chandigarh"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:border-purple-600 focus:bg-white"
            />
          </div>

          <DropMapPicker
            latitude={latitude}
            longitude={longitude}
            onLocationChange={handleLocationChange}
            claimRadius={claimRadius}
          />
        </div>

        {/* Submit Card */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm flex items-center justify-between gap-4">
          <div>
            <span className="text-xs text-slate-500 font-medium block">Save Changes</span>
            <span className="text-lg font-black text-purple-900">Update GPS Drop</span>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3.5 rounded-xl font-black text-xs text-white bg-purple-700 hover:bg-purple-800 transition-all shadow-md shadow-purple-200 flex items-center justify-center gap-2"
          >
            {loading ? (
              'Updating Drop...'
            ) : (
              <>
                <FiCheckCircle className="w-4 h-4" />
                <span>Save & Update Drop</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDrop;
